import { useEffect, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import {
  autocompleteClasses,
  Button,
  buttonClasses,
  CircularProgress,
  outlinedInputClasses,
  Typography as T,
} from "@mui/material";
import useTags from "datasets/pages/tables/hooks/useTags";
import { PlusIcon } from "~/assets/icons";
import AutocompleteInput from "~/components/AutocompleteInput";
import TooltipChip from "~/components/TooltipChip";

interface TagsAutocompleteProps {
  /** Disable the button/input */
  disabled?: boolean;
  name?: string;
  /** Called when the selection changes */
  onSave: (newValue: string[]) => void;
  /** Force the component into a read-only state */
  readOnly?: boolean;
  /** Currently‐selected tag FQN strings (e.g. ["Animals.Emu", "Privacy.PHI"]) */
  value: string[];
}

/**
 * A fully controlled tag‐picker.  Internally, it fetches all available tags via useTags(),
 * and then shows either an “Add” button or an Autocomplete (with multiple & freeSolo disabled → only existing tags).
 *
 * Parent must pass `value` + `onSave` to control it.
 */
const TagsAutocomplete = ({
  disabled = false,
  name,
  onSave,
  readOnly = false,
  value,
}: TagsAutocompleteProps) => {
  const { data: availableTags } = useTags();
  const [isEditing, setIsEditing] = useState(false);
  // local copy to prevent mutating props.value directly
  const [localValue, setLocalValue] = useState<string[]>(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (_: any, newVals: string[]) => {
    setLocalValue(newVals);
  };

  const handleClose = () => {
    setIsEditing(false);
    const changed = localValue.length !== value.length || localValue.some((t, i) => t !== value[i]);

    if (changed) {
      onSave(localValue);
    }
  };

  return (
    <>
      {(Array.isArray(localValue) && localValue?.length > 0) || isEditing ? (
        <AutocompleteInput<string, true, true>
          readOnly={readOnly}
          autoFocus={isEditing}
          disableClearable
          disabled={disabled}
          fullWidth
          getOptionLabel={(option) => option.replace(".", " / ")}
          limitTags={-1}
          multiple
          name={name ?? "tags-autocomplete"}
          onChange={handleChange}
          onClose={handleClose}
          onOpen={() => setIsEditing(true)}
          openOnFocus
          options={availableTags?.map((t) => t.fullyQualifiedName) || []}
          placeholder={!localValue.length ? "Search tags..." : ""}
          popupIcon={
            readOnly ? (
              false
            ) : disabled ? (
              <CircularProgress size={18} />
            ) : (
              <ExpandMore sx={{ fontSize: 18 }} />
            )
          }
          renderTags={(value, getTagProps) => {
            return value.map((option, index) => {
              const { key, onDelete, ...tagProps } = getTagProps({ index });
              return (
                <TooltipChip
                  {...tagProps}
                  key={key}
                  label={option.replace(".", " / ")}
                  onDelete={isEditing ? onDelete : undefined}
                  size="small"
                />
              );
            });
          }}
          size="small"
          sx={{
            [`.${autocompleteClasses.endAdornment}`]: {
              top: "50%",
              transform: "translateY(-50%)",
            },
            [`.${autocompleteClasses.tag}`]: { m: "unset" },
            [`.${outlinedInputClasses.root}`]: {
              gap: 0.5,
              minHeight: 36,
              "&.Mui-disabled": {
                display: "inline-flex",
              },
            },
            [`.${autocompleteClasses.input}`]: {
              "&.Mui-disabled": {
                overflow: "hidden",
              },
            },
          }}
          value={localValue}
        />
      ) : readOnly ? (
        <T variant="body2" fontStyle="italic" sx={{ opacity: 0.5 }}>
          No tags
        </T>
      ) : (
        <Button
          variant="text"
          size="small"
          disabled={disabled}
          startIcon={<PlusIcon />}
          sx={{
            fontSize: 13,
            color: "primary.main",
            p: 1,
            [`.${buttonClasses.startIcon}`]: { ml: "unset" },
          }}
          onClick={() => setIsEditing(true)}
        >
          Add
        </Button>
      )}
    </>
  );
};

export default TagsAutocomplete;
